import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useJourney } from '../../context/JourneyContext';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { ArrowLeft, ArrowRight, Heart, CheckCircle, School, Users, Quote } from 'lucide-react';

const NEEDS_BY_TYPE = {
  'Primary or Intermediate School': {
    title: 'Building Confidence Across the Whole School',
    description: 'Let\'s Siva works best for schools that:',
    checkmarks: [
      'Value confidence building',
      'Want structured, safe facilitation',
      'Align with Arts curriculum',
      'Want whole-school unity',
      'Value long-term partnerships'
    ],
    affirmation: 'Built and sustained through long-term school partnerships and community support.'
  },
  'High School': {
    title: 'Structured, Systemised Movement Delivery',
    description: 'Let\'s Siva works best for high schools that:',
    checkmarks: [
      'Want structured, systemised delivery',
      'Value confidence development',
      'Need engagement and attendance support',
      'Want leadership building opportunities',
      'Seek performance excellence'
    ],
    affirmation: 'Structure and system created individually to fulfil your school\'s specific needs.'
  },
  'Teacher / Leadership PL': {
    title: 'Leadership & Wellbeing',
    description: 'Our PL programs work best for educators who:',
    checkmarks: [
      'Want to develop leadership skills',
      'Value movement-based learning',
      'Seek practical, applicable strategies',
      'Want to build student confidence',
      'Are open to new approaches'
    ],
    affirmation: 'We also offer koha-based PL sessions for teachers and senior leaders in selected contexts.'
  },
  'Company or Workplace': {
    title: 'Book a session',
    description: 'We\'ll guide you to the right session option for your people in under a minute.',
    checkmarks: [
      'Value team cohesion',
      'Want to improve confidence and communication',
      'Believe in movement-based wellbeing',
      'Seek memorable team experiences',
      'Are open to trying something different'
    ],
    affirmation: 'Energizing teams through movement, reducing stress, and building stronger connections.'
  },
  'Partnership or Sponsorship': {
    title: 'Book a session',
    description: 'Choose a pathway and we\'ll show the options that fit your context.',
    checkmarks: [
      'Want to support schools and communities',
      'Value education and wellbeing initiatives',
      'Seek meaningful social impact',
      'Want aligned brand association',
      'Believe in long-term relationships'
    ],
    affirmation: 'Multi-year aligned collaborations that create real impact.'
  }
};

const STATS = [
  { value: '30+', label: 'Schools Across Aotearoa' },
  { value: '16,000+', label: 'Students Reached' },
];

const TESTIMONIAL = {
  quote: "Let's Siva is mana enhancing for all students and teachers… It is without hesitation that I recommend Let's Siva to be a part of any school programme.",
  source: "School Principal"
};

const YourNeed = () => {
  const navigate = useNavigate();
  const { journey, trackPageView } = useJourney();

  React.useEffect(() => {
    trackPageView('portal_your_need');
  }, [trackPageView]);

  const userType = journey?.user_type || 'Primary or Intermediate School';
  const content = NEEDS_BY_TYPE[userType] || NEEDS_BY_TYPE['Primary or Intermediate School'];
  const isSchool = userType.includes('School');

  return (
    <div 
      data-testid="your-need-screen"
      className="min-h-screen relative overflow-hidden bg-[#1C1917]"
    >
      {/* Background - clean, no texture */}
      <div className="absolute top-20 right-0 w-96 h-96 bg-accent/5 rounded-full blur-3xl" />

      {/* Content */}
      <div className="relative z-10 max-w-3xl mx-auto px-6 py-12">
        {/* Navigation - always visible */}
        <Button
          data-testid="back-btn"
          variant="ghost"
          onClick={() => navigate('/recommendation')}
          className="mb-8 text-stone-400 hover:text-white"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        {/* Progress */}
        <div className="flex items-center gap-2 mb-8">
          <div className="h-1 w-8 rounded-full bg-primary" />
          <div className="h-1 w-8 rounded-full bg-stone-700" />
          <div className="h-1 w-8 rounded-full bg-stone-700" />
          <div className="h-1 w-8 rounded-full bg-stone-700" />
          <span className="ml-4 text-sm text-stone-400">Step 1 of 4</span>
        </div>

        {/* Stats banner for schools */}
        {isSchool && (
          <div className="mb-8 animate-fade-in-up">
            <div className="bg-stone-900/80 border border-stone-800 rounded-2xl p-6">
              <h3 className="text-sm font-medium text-stone-500 uppercase tracking-wider mb-4 text-center">
                Trusted by School Leaders
              </h3>
              <div className="flex justify-center gap-12">
                {STATS.map((stat, index) => (
                  <div key={index} className="text-center">
                    <p className="text-3xl font-heading font-bold text-primary">{stat.value}</p>
                    <p className="text-sm text-stone-400">{stat.label}</p>
                  </div>
                ))}
              </div>
              <p className="text-center text-stone-500 text-sm mt-4">
                Leadership-endorsed partnerships across primary, intermediate, and high school settings.
              </p>
            </div>
          </div>
        )}

        {/* Main content */}
        <div className="bg-stone-900/80 border border-stone-800 rounded-3xl p-8 md:p-12 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
          {/* Icon */}
          <div className="w-14 h-14 rounded-2xl bg-accent/20 flex items-center justify-center mb-6">
            {isSchool ? <School className="w-7 h-7 text-accent" /> : <Heart className="w-7 h-7 text-accent" />}
          </div>

          <h1 
            data-testid="need-title"
            className="text-3xl sm:text-4xl font-heading font-bold text-white mb-4"
          >
            {content.title}
          </h1>

          <p className="text-stone-400 text-lg mb-6">
            {content.description}
          </p>

          {/* Checkmarks list */}
          <div className="space-y-3 mb-8">
            {content.checkmarks.map((item, index) => (
              <div 
                key={index}
                className="flex items-start gap-3 animate-slide-in-right opacity-0"
                style={{ animationDelay: `${0.2 + index * 0.08}s`, animationFillMode: 'forwards' }}
              >
                <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <p className="text-stone-300">{item}</p>
              </div>
            ))}
          </div>

          {/* Affirmation - readable contrast */}
          <div className="bg-stone-800/50 rounded-2xl p-6 border border-stone-700 mb-8">
            <p className="text-stone-200">
              {content.affirmation}
            </p>
          </div>

          {/* CTA */}
          <Button
            data-testid="continue-btn"
            onClick={() => {
              // Schools go to Proof page, others skip directly to Options
              const skipProof = userType === 'Company or Workplace' || userType === 'Partnership or Sponsorship';
              navigate(skipProof ? '/portal/options' : '/portal/proof');
            }}
            size="lg"
            className="rounded-full px-8 py-6 text-lg font-bold bg-primary hover:bg-primary/90 text-white transition-all duration-300 hover:scale-105 w-full sm:w-auto"
          >
            {(userType === 'Company or Workplace' || userType === 'Partnership or Sponsorship') 
              ? 'See your options' 
              : 'See our impact'}
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>

        {/* Contact info for Workplace/Partnership */}
        {(userType === 'Company or Workplace' || userType === 'Partnership or Sponsorship') && (
          <div className="mt-6 text-center animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
            <p className="text-sm text-stone-500">
              {userType === 'Partnership or Sponsorship' 
                ? 'Partnership kōrero: ' 
                : 'Prefer email? '}
              <a href="mailto:info@letssiva.com" className="text-primary hover:underline">
                info@letssiva.com
              </a>
            </p>
          </div>
        )}

        {/* Testimonial for schools */}
        {isSchool && (
          <div className="mt-8 bg-stone-900/50 border border-stone-800 rounded-2xl p-6 animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
            <Quote className="w-8 h-8 text-stone-700 mb-3" />
            <p className="text-stone-300 font-accent text-lg mb-3">
              "{TESTIMONIAL.quote}"
            </p>
            <p className="text-stone-500 text-sm">— {TESTIMONIAL.source}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default YourNeed;
